<template>
    <div class="modal add-show-modal">
        <div
            :class="[ 'modal-content add-show-content', modalClasses ]"
        >
            <base-modal-close @close-modal="hideModal" />
            <base-loader v-if="loading" />
            <transition v-else name="fade" mode="out-in">
                <show-type-select
                    v-if="! data.step"
                    @select="selectShowType"
                />

                <component
                    :is="component"
                    :data="data"
                    @back="handleBack"
                    @proceed="handleProceed"
                />
            </transition>
        </div>
    </div>
</template>
<script>
import { Step, steps } from '@/utils/config/addShowModal';
import { mapState, mapMutations } from 'vuex';
import FeedUrl from '@/components/Shows/AddShow/Steps/FeedUrl';
import modal from '@/mixins/modal';
import Payment from '@/components/Shows/AddShow/Steps/Payment';
import Plans from '@/components/Shows/AddShow/Steps/Plans';
import ShowDetails from '@/components/Shows/AddShow/Steps/ShowDetails';
import ShowInformation from '@/components/Shows/AddShow/Steps/ShowInformation';
import ShowTypeSelect from '@/components/Shows/AddShow/ShowTypeSelect';
import Success from '@/components/Shows/AddShow/Steps/Success';

export default {
    components: {
        FeedUrl,
        Payment,
        Plans,
        ShowDetails,
        ShowInformation,
        ShowTypeSelect,
        Success,
    },

    mixins: [modal],

    props: {
        showHash: {
            type: [String, Boolean],
            default: false,
        },
    },

    data: () => ({
        components: {
            [Step.FeedUrl]: 'FeedUrl',
            [Step.ShowInformation]: 'ShowInformation',
            [Step.ShowDetails]: 'ShowDetails',
            [Step.Plans]: 'Plans',
            [Step.Payment]: 'Payment',
            [Step.Success]: 'Success',
        },
    }),

    computed: {
        ...mapState({
            user: state => state.user || {},
            data: ({ UIState: { addShowModal }, shows }) => ({
                ...addShowModal,
                ...(addShowModal.useFirstShow
                    ? {
                        show: !! shows &&
                            Object.values(shows)
                                .find(({ plan }) => {
                                    if (! plan.upgradePlans) {
                                        return false;
                                    }

                                    const planSlug = addShowModal.plan?.slug ||
                                        addShowModal.planSlug;

                                    if (planSlug) {
                                        return plan.upgradePlans.includes(planSlug);
                                    }

                                    return !! plan.upgradePlans.length;
                                }),
                    }
                    : undefined),
            }),
        }),

        /**
         * Returns steps for current show type.
         *
         * @returns {Array} Steps.
         */
        loading() {
            return this.data.useFirstShow && ! this.data.show;
        },

        /**
         * Returns steps for current show type.
         *
         * @returns {Array} Steps.
         */
        steps() {
            return steps[
                this.data.type ||
                (this.show?.hosted ? 'hosted' : 'feed')
            ];
        },

        /**
         * Returns component to be rendered for current step.
         *
         * @returns {string} Component name for current step.
         */
        component() {
            return this.components[this.data.step];
        },

        /**
         * Returns modal classes.
         *
         * @returns {object} Modal classes.
         */
        modalClasses() {
            return {
                'full-width-content': this.data.step === Step.Payment,
                'medium-modal': this.data.step === Step.Success,
            };
        },
    },

    watch: {
        data: {
            immediate: true,

            /**
             * Watches data to hide modal if initialized with `useFirstShow`
             * param and no free show available.
             *
             * @param {object} data Modal data.
             */
            handler(data) {
                if (data.useFirstShow && data.show === undefined) {
                    this.hideModal();
                }
            },
        },
    },

    methods: {
        ...mapMutations(['changeUIState']),

        /**
         * Hide modal
         */
        hideModal() {
            this.changeUIState({ addShowModal: { visible: false, hash: false } });
        },

        /**
         * Selects creating Show type.
         *
         * @param {string} type Creating Show type.
         */
        selectShowType(type) {
            this.changeData({
                ...this.data,
                type,
                step: steps[type][0],
            });
        },

        /**
         * Changes step of creating process.
         *
         * @param {object} data Step of creating process.
         */
        changeData(data) {
            this.changeUIState({
                addShowModal: {
                    ...this.addShowModal,
                    ...data,
                },
            });
        },

        /**
         * Updates data from current step and changes step to next.
         *
         * @param {object} data New data from current step.
         */
        handleProceed(data) {
            const currentStepIndex = this.steps.findIndex((step) => step === this.data.step);

            if (-1 === currentStepIndex || ! this.steps[currentStepIndex + 1]) {
                this.hideModal();

                if (data.show) {
                    this.$router.push(`/shows/${data.show.hash}`);
                }

                // if (! this.user.meta_data.onboarding_finished) {
                //     this.changeUIState({ isOnboardingModalVisible: { visible: false } });
                // }

                return;
            }

            this.changeData({
                ...data,
                step: this.steps[currentStepIndex + 1],
            });
        },

        /**
         * Updates data from current step and changes step to previous.
         *
         * @param {object} data New data from current step.
         */
        handleBack(data) {
            const currentStepIndex = this.steps.findIndex((step) => step === this.data.step);

            if (-1 === currentStepIndex || ! this.steps[currentStepIndex - 1]) {
                this.hideModal();
                return;
            }

            this.changeData({
                ...data,
                step: this.steps[currentStepIndex - 1],
            });
        },
    },
};
</script>
